<template>
  <div class="home">

    <div class="calculator" v-show="calculated == false">

      <h1>{{ calculatorTitle }}</h1>

      <div class="fieldset">
        <h2 class="legend">Mass</h2>

        <p class="intro"><strong>Enter at least two measurements and use scientific notation.</strong> The letter e is used to mean "10 to the power of." For example, 1.316e5 means 1.316 * 10<sup>5</sup> which is 131600.</p>

        <div class="input-wrap">
          <label for="m1">M<sub>1</sub></label>
          <input type="text" id="m1" name="m1" v-model="m1" placeholder="1e9" pattern="[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?" v-on:change="updateM1($event.target.value)"> M<sub>&#9737;</sub>
        </div><!-- .input-wrap -->

        <div class="input-wrap">
          <label for="m2">M<sub>2</sub></label>
          <input type="text" id="m2" name="m2" v-model="m2" placeholder="1e9" pattern="[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?" v-on:change="updateM2($event.target.value)"> M<sub>&#9737;</sub>
        </div><!-- .input-wrap -->

        <div class="input-wrap">
          <label for="m_ratio">Ratio (q)</label>
          <input type="number" id="m_ratio" name="m_ratio" v-model="m_ratio" placeholder="1.00" min="0" max="1" step="0.00001" v-on:change="updateR($event.target.value)">
        </div><!-- .input-wrap -->

        <div class="input-wrap">
          <label for="total_m">Total Mass</label>
          <input type="text" id="total_m" name="total_m" v-model="total_m" placeholder="2e9" pattern="[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?" v-on:change="updateTM($event.target.value)"> M<sub>&#9737;</sub>
        </div><!-- .input-wrap -->
      </div>

      <div class="fieldset">
        <h2 class="legend">Distance or Redshift</h2>
          <div class="input-wrap">
            <label for="distance">Distance</label>
            <input type="number" id="distance" name="distance" placeholder="100.00" step="0.01" v-model="distance" :disabled="!watchers_on"> Mpc
          </div><!-- .input-wrap -->

          <div class="input-wrap">
            <label for="redshift">Redshift (z)*</label>
            <input type="number" id="redshift" name="redshift" step="0.0001" placeholder="0.0237" min="0" max="10" v-model="redshift" :disabled="!watchers_on"> z (years)
          </div><!-- .input-wrap -->

        <p class="small_note">*Assuming a Hubble constant of 70 km s<sup>-1</sup> Mpc<sup>-1</sup></p>
      </div>

      <div class="fieldset">
        <h2 class="legend">Position</h2>

          <div class="input-wrap">
            <label for="position_ra">Right Ascension</label>
            <input type="text" id="position_ra" v-model="position_ra" name="position_ra" placeholder="20h14m40.00s" pattern="[\d]{1,10}h[\d]{1,10}m[\d.]{1,100}s">
          </div><!-- .input-wrap -->

          <div class="input-wrap">
            <label for="position_dec">Declination</label>
            <input type="number" id="position_dec" v-model="position_dec" name="position_dec" placeholder="80.00" min="0" max="360" step="0.01"> °
          </div><!-- .input-wrap -->

      </div>

      <div class="fieldset">
        <h2 class="legend">Orbital Information</h2>

          <div class="input-wrap">
            <label for="orbital_p">Orbital Period</label>
            <input type="number" id="orbital_p" v-model="orbital_p" name="orbital_p" placeholder="400.00" step="0.01" min="0" :disabled="!watchers_on"> Yrs
          </div><!-- .input-wrap -->
          <div class="input-wrap">
            <label for="orbital_s">Orbital Separation</label>
            <input type="number" id="orbital_s" v-model="orbital_s" name="orbital_s" placeholder="10.00" step="0.01" min="0" :disabled="!watchers_on"> pc
        </div><!-- .input-wrap -->

        <div class="input-wrap">
          <label for="orbital_i">Orbital Inclination</label>
          <input type="number" id="orbital_i" v-model="orbital_i" name="orbital_i" placeholder="0.00" step="0.01" min="0"> °
        </div><!-- .input-wrap -->

          <div class="input-wrap">
            <label for="orbital_e">Orbital Eccentricity</label>
            <input type="number" id="orbital_e" v-model="orbital_e" name="orbital_e" placeholder="0.000" step="0.001" min="0">
          </div><!-- .input-wrap -->

      </div>

      <div class="submit-wrap"><button class="button" id="submit" v-on:click="doCalc"><span>Calculate!</span></button></div>

    </div>
    <div class="results" v-show="calculated">

      <img src="samplemap.png"><br>

      <div class="fieldset">
        <h2 class="legend">Residual Signature</h2>
        <div class="input-wrap">
          <input type="text" id="result_rs" name="result_rs" v-model="result_ds" readonly> &#956;s
        </div>
      </div>
      <div class="fieldset">
       <h2 class="legend">Detection Signifigance</h2>
        <div class="input-wrap">
          <input type="text" id="result_ds" name="result_ds" v-model="result_rs" readonly> %
        </div>
      </div>

      <p class="result_msg">A strain of this size would not be detectable by Pulsar Timing Array at this time. According to _____, Pulsar Timing should have refined their limits to be able to make a detection from this source within ____ years. Please note that this will be limited based on where this source is. <router-link to="/pulsarmaps">See our Pulsar Maps</router-link> for more information.</p>

      <p class="result_msg">A strain of this size should be within the limits of Pulsar Timing Array to make a detection from this source. Please note that this will be limited based on where this source is. <router-link to="/pulsarmaps">See our Pulsar Maps</router-link> for more information.</p>

      <p class="result_msg">A strain of this size would not be detectable by Pulsar Timing Array at this time. This would more likely be detectable using LIGO* or LISA* (with links to their pages).</p>

      <p>These calculations provided utilizing current pulsar maps. <router-link to="/pulsarmaps">Click here to view our Sensitivity Maps!</router-link></p>

      <p class="submit-wrap two">
        <button class="button" id="restart" v-on:click="startNewCalc"><span>Start New Calculation</span></button>
        <router-link to="/citations" class="blue button"><span>Cite This Page</span></router-link>
      </p>

    </div>

  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';

export default {
  name: 'home',
  props: {
    doo: String
  },
  data: function() {
    return {
      m1: '',
      m2: '',
      m_ratio: Number,
      total_m: '',
      distance: Number,
      redshift: Number,
      position_ra: '',
      position_dec: Number,
      orbital_p: Number,
      orbital_s: Number,
      orbital_i: Number,
      orbital_e: Number,
      calculated: false,
      watchers_on: true,
      result_ds: '',
      result_rs: ''
    }
  },
  methods:{
    updateM1: function(newVal){
      let m1 = Number(newVal);
      let m2 = Number(this.m2);
      let num = m2 + m1;
      this.m1 = m1.toExponential().replace('+','');
      this.total_m = num.toExponential().replace('+','');
      this.setRatio(m1,m2);
    },
    updateM2: function(newVal){
      let m1 = Number(this.m1);
      let m2 = Number(newVal);
      let num = m1 + m2;
      this.m2 = m2.toExponential().replace('+','');
      this.total_m = num.toExponential().replace('+','');
      this.setRatio(m1,m2);
    },
    updateTM: function(newVal){
      let total_m = Number(newVal);
      let m2 = Number(this.m2)
      let num = total_m - m2;
      this.total_m = total_m.toExponential().replace('+','');
      this.m1 = num.toExponential().replace('+','');
      let m1 = this.m1;
      this.setRatio(m1,m2);
    },
    updateR: function(newVal){
      this.m_ratio = newVal;
      let m1 = Number(this.m1);
      let m2 = Number(this.m2);
      if(m1 >= m2){
        this.m2 = (m1 * newVal).toExponential().replace('+','');
      }else{
        this.m1 = (m2 * newVal).toExponential().replace('+','');
      }

      this.total_m = (Number(this.m1)+Number(this.m2)).toExponential().replace('+','');
    },
    setRatio: function(m1,m2){
      var bigger, smaller;
      if(m1 >= m2){
        bigger = m1;
        smaller = m2;
      }else{
        bigger = m2;
        smaller = m1;
      }
      this.m_ratio = (smaller/bigger).toFixed(4);
    },
    getDistance: function() {
      var vars = 'action=distancecalc&redshift='+this.redshift;
      axios.get( 'https://blackhole.mindstaging.com/cgi-bin/calculate.py?'+vars )
        .then((response) => {
          this.distance = response.data.distance;
          setTimeout(() => { this.watchers_on = true; }, 350);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    getRedshift: function() {
      var vars = 'action=redshiftcalc&distance='+this.distance;
      axios.get( 'https://blackhole.mindstaging.com/cgi-bin/calculate.py?'+vars )
        .then((response) => {
          this.redshift = response.data.redshift;
          setTimeout(() => { this.watchers_on = true; }, 350);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    getOrbPer: function() {
      var vars = 'action=orbitalpercalc&orbital_s='+this.orbital_s+'&total_m='+this.total_m;
      axios.get( 'https://blackhole.mindstaging.com/cgi-bin/calculate.py?'+vars )
        .then((response) => {
          this.orbital_p = response.data.orbital_p;
          setTimeout(() => { this.watchers_on = true; }, 350);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    getOrbSep: function() {
      var vars = 'action=orbitalsepcalc&orbital_p='+this.orbital_p+'&total_m='+this.total_m;
      axios.get( 'https://blackhole.mindstaging.com/cgi-bin/calculate.py?'+vars )
        .then((response) => {
          this.orbital_s = response.data.orbital_s;
          setTimeout(() => { this.watchers_on = true; }, 350);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    doCalc: function() {
      this.calculated = true;
      this.scrollToTop(400);  

      var vars = 'action=calculate' +
        '&m1=' + this.m1 + '&m2=' + this.m2 + '&distance=' + this.distance +
        '&redshift=' + this.redshift + '&total_m=' + this.total_m +
        '&m_ratio=' + this.m_ratio + '&orbital_p=' + this.orbital_p +
        '&orbital_s=' + this.orbital_s + '&orbital_i=' + this.orbital_i +
        '&orbital_e=' + this.orbital_e + '&position_ra=' + this.position_ra +
        '&position_dec=' + this.position_dec;

      axios.get( 'https://blackhole.mindstaging.com/cgi-bin/calculate.py?'+vars )
        .then((response) => {
          this.result_ds = response.data.result_ds;
          this.result_rs = response.data.result_rs;
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    setCalculatorValues: function(val) {
      if(val == '3c66b') {
        this.watchers_on = false;
        this.m1 = '0.65e10';
        this.m2 = '0.65e10';
        this.m_ratio = '1.0000';
        this.total_m = '1.3e10';
        this.distance = 80.00;
        this.redshift = 0.0215;
        this.position_ra = '02h22m39.612s';
        this.position_dec = 43.00;
        this.orbital_p = 1.05;
        this.orbital_s = 0.00;
        this.orbital_i = 0.00;
        this.orbital_e = 0.001;
        setTimeout(() => { this.watchers_on = true; }, 350);
      }
      else if(val == 'hds') {
        this.watchers_on = false;
        this.m1 = '1e9';
        this.m2 = '1e9';
        this.m_ratio = '1.0000';
        this.total_m = '2e9';
        this.distance = 100.00;
        this.redshift = 0.0237;
        this.position_ra = '20h14m40.00s';
        this.position_dec = 80.00;
        this.orbital_p = 400.00;
        this.orbital_s = 10.00;
        this.orbital_i = 0.00;
        this.orbital_e = 0.00;
        setTimeout(() => { this.watchers_on = true; }, 350);
      }
      else {
        this.watchers_on = false;
        this.m1 = '';
        this.m2 = '';
        this.m_ratio = Number;
        this.total_m = '';
        this.distance = Number;
        this.redshift = Number;
        this.position_ra = '';
        this.position_dec = Number;
        this.orbital_p = Number;
        this.orbital_s = Number;
        this.orbital_i = Number;
        this.orbital_e = Number;
        setTimeout(() => { this.watchers_on = true; }, 350);
      }      
    },
    startNewCalc: function() {
      this.calculated = false;
      this.result_ds = '';
      this.result_rs = '';
      this.scrollToTop(300);      
    },
    scrollToTop: function(duration) {
      var scrollStep = -window.scrollY / (duration / 15),
          scrollInterval = setInterval(function(){
          if ( window.scrollY != 0 ) {
              window.scrollBy( 0, scrollStep );
          }
          else clearInterval(scrollInterval); 
      },15);       
    }
  },
  mounted: function() {
    this.setCalculatorValues(this.doo);
  },
  computed: {
    calculatorTitle: function() {
      let val = this.doo;
      if(val == '3c66b') {
        return '3C 66B';
      }
      else if(val == 'hds') {
        return 'Hypothetical Desirable Source';
      }
      else {
        return 'Start here!';
      }
    }
  },
  watch: {
    doo: function(val) {
      this.calculated = false;
      this.setCalculatorValues(val);
    },
    redshift: {
      handler: _.debounce(function() {
        if(this.watchers_on) { this.watchers_on = false; this.getDistance(); }
      }, 250)
    },
    distance: {
      handler: _.debounce(function() {
        if(this.watchers_on) { this.watchers_on = false; this.getRedshift(); }
      }, 250)
    },
    orbital_p: {
      handler: _.debounce(function() {
        if(this.watchers_on) { this.watchers_on = false; this.getOrbSep(); }
      }, 250)
    },
    orbital_s: {
      handler: _.debounce(function() {
        if(this.watchers_on) { this.watchers_on = false; this.getOrbPer(); }
      }, 250)
    }
  }
}
</script>

<style lang="scss">
input, input:disabled { background: #fff; color: #000; border: solid 1px #000; }
input:disabled { background: #ccc; }
.calculator {
  text-align: left;
  label {
    text-transform: uppercase;
    display: block;
  }
  .fieldset { margin-bottom: 10px; }
  .legend {
    color: #fd7;
    font-weight: bold;
    text-transform: uppercase;
  }
}
#view .fieldset .input-wrap {
  #result_rs, 
  #result_ds {
    background: #333;
    border-color: #ccc;
    color: #fff !important;
  }
}
</style>
